<template>
  <div v-if="mostrar" class="modal-overlay">
    <div class="modal-container">
      <!-- Cabeçalho -->
      <div class="modal-header">
        <h6 class="mb-0">Comunicado de falta à família</h6>
        <i class="bi bi-x-circle close-icon" @click="fechar"></i>
      </div>

      <!-- Conteúdo -->
      <div class="modal-content">
        <ul class="list-unstyled mb-3">
          <li><strong>Aluno(a):</strong> {{ falta.aluno }}</li>
          <li>
            <strong>Responsável:</strong> {{ falta.responsavel }}
            <span v-if="falta.responsavel_telefone">– Tel: {{ formatarTelefoneComDdi(falta.responsavel_telefone, falta.responsavel_telefone_ddi) }}</span>
            <span v-if="falta.responsavel_celular">– Cel: {{ formatarTelefoneComDdi(falta.responsavel_celular, falta.responsavel_celular_ddi) }}</span>
          </li>
          <li>
            <strong>{{ tituloDatasFaltas }}:</strong>
            {{ datasFaltasLabel }}
          </li>
          <li v-if="falta.turma"><strong>Turma:</strong> {{ falta.turma }}</li>
          <li v-if="falta.instrutor"><strong>Instrutor:</strong> {{ falta.instrutor }}</li>
        </ul>

        <div class="form-group mb-2">
          <label>Observação do sistema</label>
          <textarea
            class="form-control"
            rows="4"
            style="min-height: 80px;"
            :value="observacaoSistema"
            disabled
          ></textarea>
        </div>

        <div class="form-group mb-2">
          <label>Descreva o comunicado à família <span class="required">*</span></label>
          <textarea
            v-model="form.descricao"
            class="form-control"
            rows="6"
            style="min-height: 100px;"
            required
          ></textarea>
        </div>
      </div>

      <!-- Rodapé -->
      <div class="modal-footer">
        <button class="btn btn-secondary" @click="fechar">Cancelar</button>
        <button class="btn btn-primary" :disabled="isSubmitting || !form.descricao" @click="salvar">
          <span v-if="isSubmitting" class="spinner-border spinner-border-sm me-2" />
          Salvar
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, computed, getCurrentInstance } from 'vue';
import axios from '@/services/axios';
import globalUtils from '@/utils/globalUtils';

const instance = getCurrentInstance();
const gs = instance.appContext.config.globalProperties.$globalState;
const userName = gs.userName;

const props = defineProps({
  mostrar: { type: Boolean, default: false },
  falta:   { type: Object, required: true }, // objeto completo vindo do GET /operacional/faltas/:id
});
const emit = defineEmits(['close','salvou']);

const isSubmitting = ref(false);
const form = ref({ descricao: '' });

const observacaoSistema = computed(() => {
  const f = props.falta;
  if (!f) return '';

  // Se veio a lista agrupada
  if (Array.isArray(f.faltasGrupo) && f.faltasGrupo.length > 0) {
    return f.faltasGrupo
      .map(item => {
        const texto = (item.observacao || '').trim();

        // Se a observação já contém a data dentro dela, NÃO repetir
        return texto ? `- ${texto}` : `- (sem observação registrada)`;
      })
      .join('\n');
  }

  // fallback (uma única falta)
  return f.observacao || '';
});

const datasFaltasLabel = computed(() => {
  const f = props.falta;
  if (!f) return '';

  const lista = Array.isArray(f.faltasGrupo) && f.faltasGrupo.length > 0
    ? f.faltasGrupo
    : (f.data_falta ? [{ data_falta: f.data_falta }] : []);

  if (!lista.length) return '';

  const datasFmtUnicas = Array.from(
    new Set(
      lista.map(item => formatarData(item.data_falta))
    )
  );

  if (datasFmtUnicas.length === 1) {
    return datasFmtUnicas[0];
  }

  return datasFmtUnicas.join(', ');
});

const tituloDatasFaltas = computed(() => {
  const f = props.falta;
  if (!f) return 'Data da falta';

  const lista = Array.isArray(f.faltasGrupo) && f.faltasGrupo.length > 0
    ? f.faltasGrupo
    : (f.data_falta ? [{ data_falta: f.data_falta }] : []);

  if (!lista.length) return 'Data da falta';

  const datasFmtUnicas = Array.from(
    new Set(
      lista.map(item => formatarData(item.data_falta))
    )
  );

  return datasFmtUnicas.length > 1
    ? 'Datas das faltas'
    : 'Data da falta';
});


// carrega a descrição (se já existir) quando abrir
watch(() => props.falta, f => {
  form.value.descricao = f?.descricao || '';
}, { immediate: true });

function fechar() { emit('close'); }
function formatarTelefoneComDdi(numero, ddi = '') {
  const n = String(numero || '').trim();
  if (!n) return '';

  // Se o número já foi salvo com DDI no próprio texto, não duplica.
  if (/^\+\d/.test(n)) return n;

  const d = String(ddi || '').trim() || '+55';

  return `${d} ${n}`.trim();
}

function formatarData(dt) { return globalUtils.formatarData(dt); }

async function salvar() {
  if (isSubmitting.value || !form.value.descricao) return;
  isSubmitting.value = true;
  try {
    // PATCH para marcar comunicação da falta (espelha o de Queda)
    await axios.patch(`/operacional/faltas/${props.falta.id}`, {
      descricao: form.value.descricao,
      por:       userName
    });
    emit('salvou');
    fechar();
  } catch (err) {
    console.error('Erro ao salvar comunicação de falta:', err);
  } finally {
    isSubmitting.value = false;
  }
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1050;
}
.modal-container {
  background: #fff;
  width: 100%;
  max-width: 500px;
}
.modal-header {
  background: #ccc;
  padding: 10px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.close-icon {
  font-size: 1.5rem;
  cursor: pointer;
}
.modal-content {
  padding: 15px;
}
.modal-footer {
  padding: 10px 15px;
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}
.required {
  color: red;
}
.btn {
  min-width: 70px;
  padding: 6px 15px;
  font-size: 14px;
  cursor: pointer;
  border: none;
}
</style>
